import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { brandScope } from '@/lib/queryKeys';

/**
 * What the client knows about the workspace kill switch.
 *
 * There is no `GET /workspaces/{id}` yet, so the shell cannot *read* the pause
 * state — it can only observe it. `known: false` therefore means "no evidence
 * either way", and the banner stays hidden rather than guessing, because a
 * banner that appears on a hunch trains people to ignore it.
 *
 * Evidence arrives from exactly two places, both authoritative:
 *   - the `pause`/`resume` responses, which state `external_actions_allowed`;
 *   - any request the server refuses with `WORKSPACE_PAUSED`.
 */
export interface PauseState {
  readonly known: boolean;
  readonly paused: boolean;
  readonly pauseEpoch: number | null;
  /** The server's own wording. Never paraphrased — see `PauseResponse.note`. */
  readonly note: string | null;
  readonly observedAt: string | null;
}

export const UNKNOWN_PAUSE_STATE: PauseState = {
  known: false,
  paused: false,
  pauseEpoch: null,
  note: null,
  observedAt: null,
};

export function pauseStateKey(workspaceId: string) {
  return [...brandScope(workspaceId, null), 'pause-state'] as const;
}

/**
 * True for the pause-state cache entry.
 *
 * It lives under the workspace prefix like everything else, but it is an
 * *observation log*, not a fetchable resource: a blanket
 * `invalidateQueries({ queryKey: workspaceScope(id) })` would re-run its
 * query function and erase the very observation that justified the banner.
 */
export function isPauseStateKey(key: readonly unknown[]): boolean {
  return key[key.length - 1] === 'pause-state';
}

export function usePauseState(workspaceId: string): PauseState {
  const { data } = useQuery({
    queryKey: pauseStateKey(workspaceId),
    // Never fetched: the cache is the only store, seeded by observations.
    queryFn: () => UNKNOWN_PAUSE_STATE,
    initialData: UNKNOWN_PAUSE_STATE,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data;
}

export function recordPauseObservation(
  client: QueryClient,
  workspaceId: string,
  next: Omit<PauseState, 'known' | 'observedAt'>,
): void {
  client.setQueryData<PauseState>(pauseStateKey(workspaceId), {
    ...next,
    known: true,
    observedAt: new Date().toISOString(),
  });
}

/**
 * Folds `GET workspaces/{id}` into the observation log.
 *
 * The workspace read is authoritative too, but it carries no `note`, so it is
 * applied only when it *changes* what is known:
 *   - the server says paused and we did not know it (or knew otherwise, or a
 *     newer epoch): record the pause, keeping a note we already hold;
 *   - the server says running while we believed paused: record the resume.
 * "Running, and we had no evidence either way" records nothing, so the
 * status line keeps its plain wording rather than gaining an epoch nobody
 * asked about.
 */
export function useSyncPauseFromWorkspace(
  workspaceId: string,
  workspace: { readonly global_pause: boolean; readonly pause_epoch: number } | undefined,
): void {
  const client = useQueryClient();
  const globalPause = workspace?.global_pause;
  const epoch = workspace?.pause_epoch;
  useEffect(() => {
    if (globalPause === undefined || epoch === undefined) return;
    const known =
      client.getQueryData<PauseState>(pauseStateKey(workspaceId)) ?? UNKNOWN_PAUSE_STATE;
    if (globalPause) {
      if (known.known && known.paused && known.pauseEpoch === epoch) return;
      recordPauseObservation(client, workspaceId, {
        paused: true,
        pauseEpoch: epoch,
        note: known.known && known.paused ? known.note : null,
      });
    } else if (known.known && known.paused) {
      recordPauseObservation(client, workspaceId, { paused: false, pauseEpoch: epoch, note: null });
    }
  }, [client, workspaceId, globalPause, epoch]);
}
